import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChart3, Users, Wrench, ChartBar, Sheet, Database, LineChart, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
// Mobile-only bottom navigation bar. Replaces the old hamburger + drawer.
// Mirrors the parent Snipe app's BottomNav: a fixed icon bar with an Account
// tab that opens an upward popup (email, datasets, sign out).
const NAV_ITEMS = [
  { name: 'Player Leaderboard', label: 'Leaderboard', path: '/dashboard', icon: BarChart3 },
  { name: 'Vote Progression', label: 'Progress', path: '/vote-progression', icon: LineChart },
  { name: 'Team Votes', label: 'Teams', path: '/team-votes', icon: ChartBar },
  { name: 'Player Comparison', label: 'Compare', path: '/player-comparison', icon: Users },
  { name: 'Game Votes', label: 'Votes', path: '/game-votes', icon: Sheet },
  { name: 'BYOBrownlow Tool', label: 'BYO', path: '/byo-brownlow', icon: Wrench },
];

const BottomNav = ({ onOpenDatasets }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasAccess } = useAuth();
  const [showProfile, setShowProfile] = useState(false);
  const profileRef = useRef(null);
  // Same lock as the sidenav: while the coming-soon overlay is up, only the
  // Account tab stays interactive.
  const accessLocked = !hasAccess();

  useEffect(() => {
    if (!showProfile) return undefined;
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showProfile]);

  return (
    <div className={`fixed bottom-0 left-0 right-0 md:hidden ${accessLocked ? 'z-[9999]' : 'z-40'}`}>
      {/* Account popup */}
      {showProfile && (
        <div
          ref={profileRef}
          className="absolute bottom-full right-2 mb-2 w-56 bg-[#0f172a] border border-slate-700/50 rounded-xl shadow-2xl shadow-black/60 overflow-hidden animate-slide-up"
        >
          <div className="px-4 py-3 border-b border-slate-800/50">
            <p className="text-[11px] font-mono text-slate-500 uppercase tracking-widest">Account</p>
            <p className="text-xs font-mono text-slate-300 truncate mt-1">{user?.email || 'Email not found.'}</p>
          </div>
          {!accessLocked && onOpenDatasets && (
            <button
              onClick={() => {
                setShowProfile(false);
                onOpenDatasets();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-emerald-400 hover:bg-emerald-500/10 transition-colors border-b border-slate-800/50"
            >
              <Database size={16} strokeWidth={1.75} />
              <span className="text-xs font-mono font-medium">Datasets</span>
            </button>
          )}
          <button
            onClick={() => {
              setShowProfile(false);
              navigate('/logout');
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-left text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut size={16} strokeWidth={1.75} />
            <span className="text-xs font-mono font-medium">Sign out</span>
          </button>
        </div>
      )}

      {/* Bottom bar */}
      <nav className="bg-[#0a0f1e]/95 backdrop-blur-xl border-t border-slate-800/60">
        <div className="flex items-stretch justify-around px-1" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          {NAV_ITEMS.map((item) => {
            const IconComponent = item.icon;
            const isActive = !accessLocked && location.pathname === item.path;
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => navigate(item.path)}
                disabled={accessLocked}
                aria-label={item.name}
                aria-current={isActive ? 'page' : undefined}
                className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 relative transition-colors duration-150 ${
                  accessLocked ? 'text-slate-700' : isActive ? 'text-emerald-400' : 'text-slate-500 active:text-slate-300'
                }`}
              >
                {isActive && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-emerald-500 rounded-full" />
                )}
                <IconComponent className="w-5 h-5" strokeWidth={isActive ? 2.25 : 1.5} />
                <span className={`text-[9px] font-mono tracking-wide ${isActive ? 'font-semibold' : 'font-medium'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}

          {/* Account tab */}
          <button
            type="button"
            onClick={() => setShowProfile((prev) => !prev)}
            aria-haspopup="true"
            aria-expanded={showProfile}
            aria-label="Account"
            className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 relative transition-colors duration-150 ${
              showProfile ? 'text-emerald-400' : 'text-slate-500 active:text-slate-300'
            }`}
          >
            {showProfile && (
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-emerald-500 rounded-full" />
            )}
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold transition-all ${
                showProfile
                  ? 'bg-emerald-500 text-white ring-1 ring-emerald-400/50'
                  : 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'
              }`}
            >
              {(user?.email?.charAt(0) || 'A').toUpperCase()}
            </div>
            <span className={`text-[9px] font-mono tracking-wide ${showProfile ? 'font-semibold' : 'font-medium'}`}>
              Account
            </span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default BottomNav;
